import {
  CLIENT_OPTIONAL_OVERRIDES,
  EDI_TEMPLATE,
} from "./ediTemplateMock";
import {
  ELEMENT_BINDINGS,
  STATIC_ELEMENT_VALUES,
  BINDING_SAMPLE_VALUES,
  getFhirMapping,
  SEGMENT_FRIENDLY_NAMES,
} from "./fhirMappingMock";
import {
  CODE_SETS,
  ELEMENT_DROPDOWN_SOURCES,
  getCodeSetOptions,
} from "./codeSetsMock";
import { lookupElementLength } from "./elementLengthCatalog";

export { CODE_SETS, ELEMENT_DROPDOWN_SOURCES, getCodeSetOptions };

export const LOOP_DISPLAY_NAMES = {
  ENVELOPE: "Envelope",
  HEADER: "Transaction Header",
  "2000A": "Loop 2000A — Information Source",
  "2000B": "Loop 2000B — Information Receiver",
  "2000C": "Loop 2000C — Subscriber",
  "2000E": "Loop 2000E — Patient Event",
  "2000F": "Loop 2000F — Service Line",
  TRAILER: "Trailer",
};

export function getLoopDisplayName(loop) {
  return LOOP_DISPLAY_NAMES[loop.loopId] ?? loop.name ?? loop.loopId;
}

export function getVisibilityLabel(status) {
  switch (status) {
    case "visible":
      return "In EDI";
    case "optional-empty":
      return "Optional (empty)";
    case "hidden":
      return "Hidden";
    default:
      return status;
  }
}

export function getVisibilityStatus(element) {
  const hasValue = element.value != null && String(element.value).trim() !== "";
  if (hasValue) return "visible";
  if (element.required) return "hidden";
  return "optional-empty";
}

export function getSegmentFriendlyName(segmentId) {
  return SEGMENT_FRIENDLY_NAMES[segmentId] ?? segmentId;
}

/** Segment entity suffix aliases for mock key lookup (backend id → mock id). */
const SEGMENT_SUFFIX_ALIASES = {
  X3: ["X3", "PR"],
  EVT: ["EVT", ""],
  EVENT: ["EVENT", ""],
  SVC: ["SVC", ""],
};

/** Build mock lookup key candidates from canonical API element ids. */
function mockBindingKeyCandidates(elementId, segmentId) {
  const candidates = [];
  const segSuffix = segmentId.split("_").pop() ?? "";

  const threePart = elementId.match(/^([A-Z0-9]+)_([A-Z0-9]+)_(\d+)$/);
  if (threePart) {
    const [, tag, suffix, position] = threePart;
    const pos = position.padStart(2, "0");
    const shortKey = `${tag}${pos}`;
    const aliases = SEGMENT_SUFFIX_ALIASES[suffix] ?? [suffix];

    candidates.push(`${shortKey}_${suffix}`, shortKey);
    aliases.forEach((alias) => {
      if (alias) candidates.push(`${shortKey}_${alias}`);
    });
  }

  const twoPart = elementId.match(/^([A-Z0-9]+)_(\d+)$/);
  if (twoPart) {
    const [, tag, position] = twoPart;
    candidates.push(`${tag}${position.padStart(2, "0")}`);
  }

  if (/^[A-Z]+\d+$/.test(elementId)) {
    candidates.push(`${elementId}_${segSuffix}`);
    (SEGMENT_SUFFIX_ALIASES[segSuffix] ?? []).forEach((alias) => {
      if (alias) candidates.push(`${elementId}_${alias}`);
    });
  }

  candidates.push(elementId, `${elementId}_${segSuffix}`);

  return [...new Set(candidates.filter(Boolean))];
}

/** Infer X12 element position from canonical or legacy element ids (e.g. NM1_1P_04 → 4). */
export function inferElementPosition(elementId) {
  if (!elementId) return 0;

  const threePart = elementId.match(/^([A-Z0-9]+)_([A-Z0-9]+)_(\d+)$/);
  if (threePart) return parseInt(threePart[3], 10);

  const tagPos = elementId.match(/^([A-Z0-9]+?)(\d{2})$/);
  if (tagPos) return parseInt(tagPos[2], 10);

  const trailing = elementId.match(/_(\d+)$/);
  if (trailing) return parseInt(trailing[1], 10);

  return 0;
}

/** Infer X12 segment tag from element id when segment.tag is unavailable. */
export function inferSegmentTag(elementId, segmentTag) {
  if (segmentTag) return String(segmentTag).trim().toUpperCase();
  if (!elementId) return "";

  const threePart = elementId.match(/^([A-Z0-9]+)_([A-Z0-9]+)_(\d+)$/);
  if (threePart) return threePart[1];

  const twoPart = elementId.match(/^([A-Z0-9]+)_(\d+)$/);
  if (twoPart) return twoPart[1];

  const tagPos = elementId.match(/^([A-Z0-9]+?)(\d{2})$/);
  if (tagPos) return tagPos[1];

  return "";
}

/** Build TR3 refDes from segment tag + element position (e.g. NM1 + 1 → NM101). */
export function buildElementRefDes(segmentTag, position) {
  const tag = String(segmentTag || "").trim().toUpperCase();
  const pos = Number(position);
  if (!tag || !Number.isFinite(pos) || pos <= 0) return null;
  return `${tag}${String(Math.trunc(pos)).padStart(2, "0")}`;
}

function toOptionalLength(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Resolve min/max length for an explorer element.
 * Prefers API/element fields; falls back to TR3 catalog via tag+position → refDes.
 */
export function resolveElementLengths(element, segmentTag) {
  const minFromEl = toOptionalLength(element?.minLength);
  const maxFromEl = toOptionalLength(element?.maxLength);
  if (minFromEl != null || maxFromEl != null) {
    return { minLength: minFromEl, maxLength: maxFromEl };
  }

  const position =
    element?.position ??
    element?.Position ??
    inferElementPosition(element?.elementId);
  const tag = inferSegmentTag(element?.elementId, segmentTag);
  const refDes = buildElementRefDes(tag, position);
  const fromCatalog = lookupElementLength(refDes);
  if (!fromCatalog) {
    return { minLength: null, maxLength: null };
  }
  return {
    minLength: toOptionalLength(fromCatalog.minLength),
    maxLength: toOptionalLength(fromCatalog.maxLength),
  };
}

/**
 * Validate sample value length against min/max.
 * Empty value skips (no error). Returns message string or null.
 */
export function validateSampleLength(value, minLength, maxLength) {
  if (value == null || String(value) === "") return null;

  const len = String(value).length;
  const min = toOptionalLength(minLength);
  const max = toOptionalLength(maxLength);

  if (min != null && max != null) {
    if (len < min || len > max) {
      return min === max
        ? `Length must be ${min}`
        : `Length must be ${min}–${max}`;
    }
    return null;
  }
  if (min != null && len < min) {
    return `Must be at least ${min} character${min === 1 ? "" : "s"}`;
  }
  if (max != null && len > max) {
    return `Must be at most ${max} character${max === 1 ? "" : "s"}`;
  }
  return null;
}

/**
 * Pad/truncate a non-empty sample so it satisfies TR3 min/max length.
 * Empty stays empty (do not invent values for blank optionals).
 * Short values are right-padded with '0' (ID-like / sender fields).
 */
export function fitSampleToLength(value, minLength, maxLength) {
  if (value == null || String(value) === "") return "";

  let s = String(value);
  const min = toOptionalLength(minLength);
  const max = toOptionalLength(maxLength);

  if (max != null && s.length > max) {
    s = s.slice(0, max);
  }

  if (min != null && s.length < min) {
    const target = max != null ? Math.min(min, max) : min;
    if (s.length < target) {
      s = s.padEnd(target, "0");
    }
  }

  return s;
}

function lookupMockBinding(elementId, segmentId) {
  for (const key of mockBindingKeyCandidates(elementId, segmentId)) {
    if (ELEMENT_BINDINGS[key]) return ELEMENT_BINDINGS[key];
  }
  return null;
}

function inferBindingFromApi(element) {
  if (element.binding) return element.binding;

  const display = (element.sourceDisplay ?? "").trim();
  if (!display || display === "—" || display.startsWith("Static")) return null;

  // Backend MapElement sets sourceDisplay to binding for model/computed/clientConfig elements.
  if (element.sourceType === "dynamic") {
    return display;
  }

  if (element.sourceType === "system") {
    if (display.includes(".")) return display;
    if (/^[A-Z][a-zA-Z0-9]+$/.test(display)) return display;
  }

  return null;
}

function resolveBinding(element, segmentId) {
  const fromApi = inferBindingFromApi(element);
  if (fromApi) return fromApi;

  const fromMock = lookupMockBinding(element.elementId, segmentId);
  if (fromMock) return fromMock;

  if (element.elementId.startsWith("ISA") || element.elementId.startsWith("GS")) {
    if (element.label.includes("Sender")) return "Envelope.SenderId";
    if (element.label.includes("Receiver")) return "Envelope.ReceiverId";
    if (element.label.includes("Usage Indicator")) return "Envelope.UsageIndicator";
  }
  if (element.elementId === "BHT02" || element.elementId === "BHT_02") {
    return "BhtPurposeCode";
  }
  if (element.elementId.startsWith("ST")) return null;
  return null;
}

function resolveStaticMeta(elementId, segmentId) {
  const suffix = segmentId.split("_").pop();
  return (
    STATIC_ELEMENT_VALUES[elementId] ??
    STATIC_ELEMENT_VALUES[`${elementId}_${suffix}`] ??
    null
  );
}

function isInternalModelPath(path, binding) {
  const text = String(path ?? "").trim();
  if (!text || text === "—") return true;

  // Explorer arrow / Bundle / resource-rooted FHIR paths are OK.
  if (text.includes("→") || text.includes("->")) return false;
  if (/^Bundle\./i.test(text)) return false;

  const fhirRoots = new Set([
    "Claim",
    "Coverage",
    "Patient",
    "Practitioner",
    "Organization",
    "Location",
    "DocumentReference",
    "PractitionerRole",
    "Condition",
    "Encounter",
  ]);
  const root = text.split(/[.\s[(]/)[0];
  if (fhirRoots.has(root)) return false;

  const normalizedBinding = String(binding ?? "").trim();
  if (normalizedBinding && text === normalizedBinding) return true;

  // Internal model targets look like Dependent.BirthDate / Requester.LastName.
  if (/^[A-Z][A-Za-z0-9]*(\.[A-Z][A-Za-z0-9]*)+$/.test(text)) return true;

  return false;
}

function resolveDisplayFhirPath(element, binding, fhir) {
  const candidates = [
    element?.fhirPath,
    fhir?.fhirPath,
    element?.sourceDisplay,
  ];
  for (const candidate of candidates) {
    const text = String(candidate ?? "").trim();
    if (!text || text === "—") continue;
    if (isInternalModelPath(text, binding)) continue;
    return text;
  }
  return null;
}

function classifyElementSource(element, segmentId, binding, fhir) {
  const rawFhirPath = resolveDisplayFhirPath(element, binding, fhir);
  if (rawFhirPath) {
    const path = String(rawFhirPath)
      .replace(/Coverage\.subscriber → Patient\./, "Patient.")
      .replace(/Claim\.provider → Practitioner\./, "Practitioner.")
      .replace(/Claim\.provider → Organization\./, "Organization.")
      .replace(/Claim\.facility → Location\./, "Location.")
      .replace(/Claim\.careTeam → Practitioner\./, "Practitioner.")
      .replace(/Claim\.careTeam → Organization\./, "Organization.")
      .replace(/tripLocation → Location\./, "Location.");
    return { sourceType: "dynamic", sourceDisplay: path };
  }

  if (fhir?.defaultValue && !fhir?.transform) {
    return {
      sourceType: "static",
      sourceDisplay: `Static ("${fhir.defaultValue}")`,
    };
  }

  if (
    fhir?.transform ||
    fhir?.source === "computed" ||
    fhir?.source === "clientConfig" ||
    element?.source === "computed" ||
    element?.source === "clientConfig" ||
    (binding && String(binding).startsWith("Envelope.")) ||
    binding === "GeneratedAt" ||
    binding === "ControlNumber" ||
    binding === "FunctionalGroupDate" ||
    binding === "FunctionalGroupTime" ||
    binding === "InterchangeDate" ||
    binding === "InterchangeTime" ||
    binding === "TransactionSetControlNumber" ||
    binding === "SegmentCount" ||
    binding === "HierarchicalId" ||
    binding === "HierarchicalParentId" ||
    element?.sourceType === "system"
  ) {
    if (
      fhir?.source === "clientConfig" ||
      element?.source === "clientConfig" ||
      (binding && String(binding).startsWith("Envelope."))
    ) {
      return { sourceType: "system", sourceDisplay: "Client configuration" };
    }
    if (fhir?.transform) {
      return { sourceType: "system", sourceDisplay: `System (${fhir.transform})` };
    }
    return { sourceType: "system", sourceDisplay: "System generated" };
  }

  // Dynamic with binding but no FHIR path yet — keep Dynamic badge; leave path empty
  // rather than showing Dependent.BirthDate-style model names.
  if (binding || element.sourceType === "dynamic") {
    return {
      sourceType: "dynamic",
      sourceDisplay: "",
    };
  }

  if (element.sourceType === "system") {
    const display = (element.sourceDisplay ?? "").trim();
    if (display && display !== "—" && !isInternalModelPath(display, binding)) {
      return { sourceType: "system", sourceDisplay: display };
    }
  }

  const staticMeta = resolveStaticMeta(element.elementId, segmentId);
  if (staticMeta) {
    return { sourceType: "static", sourceDisplay: staticMeta.label };
  }

  const val = element.value != null ? String(element.value).trim() : "";
  if (val) {
    return {
      sourceType: "static",
      sourceDisplay: `Static ("${val}")`,
    };
  }

  return { sourceType: "static", sourceDisplay: "Static" };
}

function isPlaceholderSampleValue(value) {
  const v = String(value ?? "").trim();
  return v === "" || v === "SAMPLE" || v === "—";
}

function inferFriendlySampleValue(element, binding) {
  if (binding && Object.prototype.hasOwnProperty.call(BINDING_SAMPLE_VALUES, binding)) {
    return BINDING_SAMPLE_VALUES[binding];
  }

  const fhir = binding ? getFhirMapping(binding) : null;
  if (fhir?.defaultValue != null && String(fhir.defaultValue).trim() !== "") {
    return String(fhir.defaultValue).trim();
  }

  const label = (element.friendlyLabel ?? element.label ?? "").toLowerCase();
  const dataType = (element.dataType ?? "").toLowerCase();

  if (
    binding === "SegmentCount" ||
    label.includes("segment count") ||
    label.includes("number of included segments") ||
    label.includes("transaction set count") ||
    dataType === "integer" ||
    dataType === "n0"
  ) {
    return binding === "SegmentCount" || label.includes("segment") ? "18" : "1";
  }

  if (label.includes("first name")) return "JOHN";
  if (label.includes("middle name")) return "M";
  if (label.includes("last name") || label.includes("org name")) return "DOE";
  if (label.includes("phone")) return "5551234567";
  if (label.includes("npi") || label.includes("identifier")) return "1234567890";
  if (label.includes("member id")) return "MEM123456789";
  if (label.includes("birth date")) return "19850315";
  if (label.includes("gender")) return "M";
  if (label.includes("postal") || label.includes("zip")) return "19087";
  if (label.includes("state")) return "PA";
  if (label.includes("city")) return "ANYTOWN";
  if (label.includes("address line 1")) return "123 MAIN ST";
  if (label.includes("address line 2")) return "SUITE 100";
  if (label.includes("diagnosis")) return "ABK:I219";
  if (label.includes("procedure")) return "HC:99213";
  if (label.includes("charge") || label.includes("amount")) return "150.00";
  if (label.includes("quantity")) return "1";
  if (label.includes("message")) return "Clinical documentation note";
  if (label.includes("taxonomy")) return "207Q00000X";
  if (label.includes("date") || dataType === "date") return "20250722";
  if (label.includes("time") || dataType === "time") return "1430";
  if (dataType === "code" || dataType === "id") return "XX";

  if (binding) {
    const leaf = binding.split(".").pop() ?? binding;
    return leaf.replace(/([a-z])([A-Z])/g, "$1 $2");
  }

  return "Example value";
}

function isClientConfigElement(element, binding = null) {
  if (element?.source === "clientConfig" || element?.source === "client") {
    return true;
  }
  const b = binding ?? element?.binding;
  return typeof b === "string" && b.startsWith("Envelope.");
}

/**
 * Validate client configuration sample against TR3 min and max length.
 * Short values (e.g. AANEEL when min is 15) must show an error and block Save.
 */
export function validateClientConfigSampleLength(value, minLength, maxLength) {
  return validateSampleLength(value, minLength, maxLength);
}

export function resolveElementSampleValue(element, segmentId, segmentTag) {
  const binding = element.binding ?? resolveBinding(element, segmentId);
  const isClientConfig = isClientConfigElement(element, binding);

  // Client configuration: live Client.json value only — never invent BINDING_SAMPLE / pad.
  if (isClientConfig) {
    const rawExisting =
      element.value != null && String(element.value).trim() !== ""
        ? String(element.value).trim()
        : "";
    if (rawExisting && !isPlaceholderSampleValue(rawExisting)) {
      return rawExisting;
    }
    const defaultVal = element.defaultValue ?? element.DefaultValue;
    if (
      defaultVal != null &&
      String(defaultVal).trim() !== "" &&
      !isPlaceholderSampleValue(defaultVal)
    ) {
      return String(defaultVal).trim();
    }
    return "";
  }

  let resolved = "";

  const rawExisting =
    element.value != null && String(element.value).trim() !== ""
      ? String(element.value).trim()
      : "";
  if (rawExisting && !isPlaceholderSampleValue(rawExisting)) {
    resolved = rawExisting;
  } else if (
    binding === "HierarchicalParentId" ||
    (element.source === "static" &&
      (element.defaultValue === "" || element.DefaultValue === ""))
  ) {
    // TR3 NOT USED / no parent — keep empty (never SAMPLE / "Example value").
    resolved =
      binding &&
      Object.prototype.hasOwnProperty.call(BINDING_SAMPLE_VALUES, binding)
        ? String(BINDING_SAMPLE_VALUES[binding] ?? "")
        : "";
  } else {
    const defaultVal = element.defaultValue ?? element.DefaultValue;
    if (
      defaultVal != null &&
      String(defaultVal).trim() !== "" &&
      !isPlaceholderSampleValue(defaultVal)
    ) {
      resolved = String(defaultVal).trim();
    } else {
      const staticMeta = resolveStaticMeta(element.elementId, segmentId);
      if (staticMeta?.value) {
        resolved = staticMeta.value;
      } else if (binding) {
        resolved = inferFriendlySampleValue(element, binding);
      } else if (element.sourceType === "system") {
        resolved = "000000001";
      } else {
        resolved = inferFriendlySampleValue(element, null);
      }
    }
  }

  // Preserve intentional empty samples (e.g. HL02 with no parent).
  if (resolved === "" || resolved == null) return "";

  const { minLength, maxLength } = resolveElementLengths(element, segmentTag);
  return fitSampleToLength(resolved, minLength, maxLength);
}

export function enrichElement(element, segmentId, segmentTag, options = {}) {
  const useMockFhir = options.useMockFhir !== false;
  const binding =
    element.binding ?? resolveBinding(element, segmentId);
  const fhir = binding ? getFhirMapping(binding) : null;
  // Catalog FHIR paths are used for display defaults; saved API/override paths always win.
  // Never surface internal model bindings (Dependent.BirthDate) as the path.
  const displayFhirPath = resolveDisplayFhirPath(element, binding, fhir);
  const visibilityStatus = getVisibilityStatus(element);
  const classified = classifyElementSource(element, segmentId, binding, fhir);
  const sourceType =
    !useMockFhir && element.sourceType === "dynamic"
      ? "dynamic"
      : !useMockFhir && element.sourceType && !displayFhirPath
        ? element.sourceType
        : classified.sourceType;
  const sourceDisplay = displayFhirPath || classified.sourceDisplay;
  const friendlyLabel =
    element.friendlyLabel ??
    element.label
      .replace(/Subscriber |Requester |Payer /, "")
      .replace(/Identification Code Qualifier/, "ID Qualifier")
      .replace(/Entity Identifier Code/, "Entity Code");
  const position =
    element.position ??
    element.Position ??
    inferElementPosition(element.elementId);

  const dropdownSource =
    element.dropdownSource ||
    ELEMENT_DROPDOWN_SOURCES[element.elementId] ||
    null;

  const { minLength, maxLength } = resolveElementLengths(
    { ...element, position },
    segmentTag
  );

  const COMPUTED_BINDINGS = new Set([
    "GeneratedAt",
    "ControlNumber",
    "FunctionalGroupDate",
    "FunctionalGroupTime",
    "InterchangeDate",
    "InterchangeTime",
    "TransactionSetControlNumber",
    "SegmentCount",
    "HierarchicalId",
    "HierarchicalParentId",
  ]);

  const source =
    element.source ||
    fhir?.source ||
    (binding && String(binding).startsWith("Envelope.")
      ? "clientConfig"
      : null) ||
    (binding && COMPUTED_BINDINGS.has(binding) ? "computed" : null) ||
    (binding ? "model" : "static");

  const enriched = {
    ...element,
    binding,
    position,
    minLength,
    maxLength,
    sourceType,
    sourceDisplay,
    source,
    // Saved override / API path first; else catalog FHIR path. Never model binding names.
    fhirPath: displayFhirPath,
    fhirTransform: fhir?.transform ?? null,
    fhirNotes: fhir?.notes ?? null,
    fallbackPath: fhir?.fallbackPath ?? null,
    dropdownSource,
    visibilityStatus,
    isVisible: visibilityStatus === "visible",
    friendlyLabel,
  };

  return {
    ...enriched,
    value: resolveElementSampleValue(enriched, segmentId, segmentTag),
  };
}

export function enrichSegment(segment) {
  return {
    ...segment,
    friendlyName: getSegmentFriendlyName(segment.segmentId),
    elements: segment.elements.map((el) =>
      enrichElement(el, segment.segmentId, segment.tag)
    ),
  };
}

export function enrichLoop(loop) {
  return {
    ...loop,
    segments: loop.segments.map(enrichSegment),
  };
}

export function getDefaultSegmentVisibility(template) {
  const map = {};
  template.loops.forEach((loop) => {
    loop.segments.forEach((seg) => {
      if (!seg.isOptional) {
        map[seg.segmentId] = true;
      } else {
        map[seg.segmentId] = !!(seg.raw && String(seg.raw).trim());
      }
    });
  });
  return map;
}

export const SEGMENT_VISIBILITY_STORAGE_KEY =
  "edi-mapping-explorer-segment-visibility";

export function loadSegmentVisibility(template) {
  const defaults = getDefaultSegmentVisibility(template);

  try {
    const raw = localStorage.getItem(SEGMENT_VISIBILITY_STORAGE_KEY);
    if (!raw) return defaults;

    const saved = JSON.parse(raw);
    if (!saved || typeof saved !== "object") return defaults;

    const merged = { ...defaults };
    Object.keys(saved).forEach((segmentId) => {
      if (Object.prototype.hasOwnProperty.call(merged, segmentId)) {
        merged[segmentId] = saved[segmentId] !== false;
      }
    });
    return merged;
  } catch {
    return defaults;
  }
}

export function getOptionalSegmentVisibility(template, visibility) {
  const optionalOnly = {};
  template.loops.forEach((loop) => {
    loop.segments.forEach((seg) => {
      if (seg.isOptional === true) {
        optionalOnly[seg.segmentId] = visibility[seg.segmentId] !== false;
      }
    });
  });
  return optionalOnly;
}

export function areOptionalSegmentVisibilitiesEqual(
  template,
  visibilityA,
  visibilityB
) {
  const a = getOptionalSegmentVisibility(template, visibilityA);
  const b = getOptionalSegmentVisibility(template, visibilityB);
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

export function saveSegmentVisibility(template, visibility) {
  const optionalOnly = getOptionalSegmentVisibility(template, visibility);

  try {
    localStorage.setItem(
      SEGMENT_VISIBILITY_STORAGE_KEY,
      JSON.stringify(optionalOnly)
    );
    return { ok: true, saved: optionalOnly };
  } catch {
    return { ok: false, saved: null };
  }
}

export function mapExplorerResponse(apiData) {
  if (!apiData?.loops) return { loops: [] };

  const readBool = (obj, camel, pascal) => {
    const value = obj[camel] ?? obj[pascal];
    return value === true || value === "true";
  };

  const readVisible = (obj) => {
    const value = obj.isUserVisible ?? obj.IsUserVisible;
    return value !== false && value !== "false";
  };

  const readStr = (obj, camel, pascal) => obj[camel] ?? obj[pascal] ?? "";

  return {
    transactionSet: apiData.transactionSet ?? apiData.TransactionSet,
    implementationGuide:
      apiData.implementationGuide ?? apiData.ImplementationGuide,
    codeSets: apiData.codeSets ?? apiData.CodeSets ?? CODE_SETS,
    loops: apiData.loops.map((loop) => ({
      loopId: readStr(loop, "loopId", "LoopId"),
      name: readStr(loop, "name", "Name"),
      displayName: readStr(loop, "displayName", "DisplayName"),
      segments: (loop.segments || []).map((seg) => ({
        segmentId: readStr(seg, "segmentId", "SegmentId"),
        tag: readStr(seg, "tag", "Tag"),
        loopId: readStr(seg, "loopId", "LoopId") || readStr(loop, "loopId", "LoopId"),
        sequence: seg.sequence ?? seg.Sequence ?? 0,
        repeatRule: readStr(seg, "repeatRule", "RepeatRule") || "once",
        friendlyName:
          readStr(seg, "friendlyName", "FriendlyName") ||
          readStr(seg, "description", "Description") ||
          readStr(seg, "segmentId", "SegmentId"),
        description: seg.description ?? seg.Description,
        isOptional: readBool(seg, "isOptional", "IsOptional"),
        isUserVisible: readVisible(seg),
        baseVisibilityRule: readStr(
          seg,
          "baseVisibilityRule",
          "BaseVisibilityRule"
        ),
        effectiveVisibilityRule: readStr(
          seg,
          "effectiveVisibilityRule",
          "EffectiveVisibilityRule"
        ),
        raw: readStr(seg, "sampleRaw", "SampleRaw"),
        elements: (seg.elements || seg.Elements || []).map((el) => {
          const elementId = readStr(el, "elementId", "ElementId");
          const minLength = el.minLength ?? el.MinLength ?? null;
          const maxLength = el.maxLength ?? el.MaxLength ?? null;
          const apiFhirPath = readStr(el, "fhirPath", "FhirPath") || null;
          const mapped = {
            elementId,
            label: readStr(el, "label", "Label"),
            friendlyLabel:
              readStr(el, "friendlyLabel", "FriendlyLabel") ||
              readStr(el, "label", "Label"),
            sourceType: readStr(el, "sourceType", "SourceType") || "system",
            sourceDisplay: readStr(el, "sourceDisplay", "SourceDisplay") || "—",
            source: readStr(el, "source", "Source") || null,
            binding: readStr(el, "binding", "Binding") || null,
            fhirPath: apiFhirPath || null,
            value: el.value ?? el.Value ?? "",
            required: readBool(el, "required", "Required"),
            minLength:
              minLength === "" || minLength == null ? null : Number(minLength),
            maxLength:
              maxLength === "" || maxLength == null ? null : Number(maxLength),
            position:
              el.position ??
              el.Position ??
              inferElementPosition(elementId),
            dataType: readStr(el, "dataType", "DataType") || null,
            dropdownSource:
              readStr(el, "dropdownSource", "DropdownSource") ||
              ELEMENT_DROPDOWN_SOURCES[elementId] ||
              null,
          };
          return enrichElement(
            mapped,
            readStr(seg, "segmentId", "SegmentId"),
            readStr(seg, "tag", "Tag"),
            { useMockFhir: false }
          );
        }),
      })),
    })),
  };
}

/**
 * Stamp client Overrides.json fhirMappings onto explorer elements by binding/target.
 * Prefer override path over whatever is already on the element.
 */
export function applyFhirMappingOverrides(template, overridesDoc) {
  if (!template?.loops) return template;

  const list =
    overridesDoc?.overrides?.fhirMappings ||
    overridesDoc?.overrides?.FhirMappings ||
    overridesDoc?.Overrides?.FhirMappings ||
    [];
  if (!Array.isArray(list) || list.length === 0) return template;

  const byTarget = new Map();
  list.forEach((m) => {
    const target = String(m?.target ?? m?.Target ?? "").trim();
    const fhirPath = String(m?.fhirPath ?? m?.FhirPath ?? "").trim();
    if (!target || !fhirPath) return;
    byTarget.set(target.toLowerCase(), fhirPath);
  });
  if (byTarget.size === 0) return template;

  return {
    ...template,
    loops: template.loops.map((loop) => ({
      ...loop,
      segments: (loop.segments || []).map((seg) => ({
        ...seg,
        elements: (seg.elements || []).map((el) => {
          const binding = String(el.binding ?? "").trim();
          if (!binding) return el;
          const path = byTarget.get(binding.toLowerCase());
          if (!path) return el;
          return {
            ...el,
            fhirPath: path,
            sourceDisplay: path,
            sourceType: el.sourceType === "system" ? "dynamic" : el.sourceType,
          };
        }),
      })),
    })),
  };
}

export function getSegmentVisibilityFromTemplate(template) {
  const map = {};
  (template?.loops || []).forEach((loop) => {
    loop.segments.forEach((seg) => {
      const isOptional = seg.isOptional === true;
      map[seg.segmentId] = isOptional ? seg.isUserVisible !== false : true;
    });
  });
  return map;
}

export function buildSegmentOverrideEntry(segment, loop) {
  return {
    segmentId: segment.segmentId,
    loopId: segment.loopId || loop?.loopId || "",
    sequence: segment.sequence ?? 0,
    tag: segment.tag || "",
    visibilityRule: "false",
    repeatRule: segment.repeatRule || "once",
  };
}

/** Clear sentinel — backend upsert removes this segment override (restore base visibility). */
export function buildSegmentInheritEntry(segment, loop) {
  return {
    segmentId: segment.segmentId,
    loopId: segment.loopId || loop?.loopId || "",
    sequence: segment.sequence ?? 0,
    tag: segment.tag || "",
    visibilityRule: "inherit",
    repeatRule: segment.repeatRule || "once",
  };
}

export function arePathEditsEqual(a, b) {
  const keys = new Set([
    ...Object.keys(a || {}),
    ...Object.keys(b || {}),
  ]);
  for (const key of keys) {
    if (String((a || {})[key] ?? "") !== String((b || {})[key] ?? "")) {
      return false;
    }
  }
  return true;
}

/** Collect Dynamic (FHIR) path overrides keyed by Elements binding (target). */
export function buildFhirMappingOverrides(template, pathEdits) {
  if (!pathEdits || Object.keys(pathEdits).length === 0) return null;

  const byTarget = new Map();

  (template?.loops || []).forEach((loop) => {
    (loop.segments || []).forEach((seg) => {
      (seg.elements || []).forEach((el) => {
        if (el.sourceType !== "dynamic") return;
        if (
          !Object.prototype.hasOwnProperty.call(pathEdits, el.elementId)
        ) {
          return;
        }

        const target = el.binding;
        if (!target || String(target).trim() === "") return;

        const fhirPath = String(pathEdits[el.elementId] ?? "").trim();
        const baseline = String(el.fhirPath ?? "").trim();
        if (fhirPath === baseline) return;

        byTarget.set(target, { target, fhirPath });
      });
    });
  });

  const list = Array.from(byTarget.values());
  return list.length > 0 ? list : null;
}

export function buildOverridesPayload(
  template,
  segmentVisibility,
  existingOverrides = null,
  clientConfigSamples = null,
  previousClientConfigSamples = null,
  pathEdits = null,
  previousPathEdits = null
) {
  const existingSegments = existingOverrides?.overrides?.segments || [];
  const byKey = new Map();

  existingSegments.forEach((seg) => {
    const loopId = seg.loopId || seg.LoopId || "";
    const segmentId = seg.segmentId || seg.SegmentId;
    if (!segmentId) return;
    const key = `${loopId}|${segmentId}`.toLowerCase();
    byKey.set(key, seg);
  });

  (template?.loops || []).forEach((loop) => {
    loop.segments.forEach((seg) => {
      if (seg.isOptional !== true) return;

      const loopId = seg.loopId || loop.loopId || "";
      const key = `${loopId}|${seg.segmentId}`.toLowerCase();

      if (segmentVisibility[seg.segmentId] === false) {
        byKey.set(key, buildSegmentOverrideEntry(seg, loop));
      } else if (byKey.has(key)) {
        // Upsert-safe clear: omit alone no longer removes under BE upsert-by-id.
        byKey.set(key, buildSegmentInheritEntry(seg, loop));
      }
    });
  });

  const existing = existingOverrides?.overrides || {};
  // Omit fhirMappings from the spread — only send when path edits are dirty
  // so the BE can upsert a partial list without wiping unrelated mappings.
  const {
    fhirMappings: _existingFhirMappings,
    FhirMappings: _existingFhirMappingsPascal,
    ...existingWithoutFhir
  } = existing;

  const elements = buildClientConfigElementOverrides(
    template,
    clientConfigSamples,
    existing.elements || existing.Elements || []
  );

  // Client.json envelope is source of truth for Envelope.* bindings on generate.
  // When samples are dirty, always send envelope with edited fields (not only Overrides elements).
  const samplesDirty =
    !!clientConfigSamples &&
    previousClientConfigSamples != null &&
    !areClientConfigSamplesEqual(clientConfigSamples, previousClientConfigSamples);
  const envelope = samplesDirty
    ? buildEnvelopePatchFromClientConfigSamples(
        template,
        clientConfigSamples,
        previousClientConfigSamples,
        { onlyEdited: true }
      )
    : null;

  const pathsDirty =
    pathEdits != null &&
    previousPathEdits != null &&
    !arePathEditsEqual(pathEdits, previousPathEdits);
  const fhirMappings = pathsDirty
    ? buildFhirMappingOverrides(template, pathEdits)
    : null;

  const payload = {
    overrides: {
      ...existingWithoutFhir,
      segments: Array.from(byKey.values()),
      elements,
    },
  };

  if (fhirMappings) {
    payload.overrides.fhirMappings = fhirMappings;
  }

  if (envelope && Object.keys(envelope).length > 0) {
    payload.envelope = envelope;
  }

  return payload;
}

const ENVELOPE_BINDING_TO_FIELD = {
  "Envelope.SenderId": "senderId",
  "Envelope.ReceiverId": "receiverId",
  "Envelope.UsageIndicator": "usageIndicator",
  "Envelope.TraceAssigningEntityId": "traceAssigningEntityId",
};

export function collectClientConfigElements(template) {
  const list = [];
  (template?.loops || []).forEach((loop) => {
    (loop.segments || []).forEach((seg) => {
      (seg.elements || []).forEach((el) => {
        if (isClientConfigElement(el)) {
          list.push({ ...el, segmentTag: seg.tag, segmentId: seg.segmentId });
        }
      });
    });
  });
  return list;
}

export function collectClientConfigSamplesFromTemplate(template) {
  const samples = {};
  collectClientConfigElements(template).forEach((el) => {
    if (!el.elementId) return;
    // Prefer API/element value (Client.json envelope) — do not invent padded samples.
    if (el.value != null && String(el.value).trim() !== "") {
      samples[el.elementId] = String(el.value);
      return;
    }
    const defaultVal = el.defaultValue ?? el.DefaultValue;
    samples[el.elementId] =
      defaultVal != null && String(defaultVal).trim() !== ""
        ? String(defaultVal).trim()
        : "";
  });
  return samples;
}

export function areClientConfigSamplesEqual(a, b) {
  const keys = new Set([
    ...Object.keys(a || {}),
    ...Object.keys(b || {}),
  ]);
  for (const key of keys) {
    if (String((a || {})[key] ?? "") !== String((b || {})[key] ?? "")) {
      return false;
    }
  }
  return true;
}

export function hasInvalidClientConfigSamples(template, clientConfigSamples) {
  return collectClientConfigElements(template).some((el) => {
    const value = Object.prototype.hasOwnProperty.call(
      clientConfigSamples || {},
      el.elementId
    )
      ? clientConfigSamples[el.elementId]
      : el.value ?? "";
    const { minLength, maxLength } = resolveElementLengths(el, el.segmentTag);
    return !!validateClientConfigSampleLength(value, minLength, maxLength);
  });
}

function buildClientConfigElementOverrides(
  template,
  clientConfigSamples,
  existingElements
) {
  const byId = new Map();
  (existingElements || []).forEach((el) => {
    const id = el.elementId || el.ElementId;
    if (!id) return;
    byId.set(id, {
      elementId: id,
      segmentId: el.segmentId || el.SegmentId || "",
      value: el.value ?? el.Value ?? undefined,
    });
  });

  collectClientConfigElements(template).forEach((el) => {
    if (
      !clientConfigSamples ||
      !Object.prototype.hasOwnProperty.call(clientConfigSamples, el.elementId)
    ) {
      return;
    }
    const prev = byId.get(el.elementId) || {};
    byId.set(el.elementId, {
      ...prev,
      elementId: el.elementId,
      segmentId: el.segmentId || prev.segmentId || "",
      value: clientConfigSamples[el.elementId],
    });
  });

  return Array.from(byId.values()).map((el) => ({
    elementId: el.elementId,
    ...(el.segmentId ? { segmentId: el.segmentId } : {}),
    ...(el.value != null ? { value: String(el.value) } : {}),
  }));
}

export function buildEnvelopePatchFromClientConfigSamples(
  template,
  clientConfigSamples,
  previousSamples = null,
  options = null
) {
  if (!clientConfigSamples) return null;
  const onlyEdited = options?.onlyEdited === true;
  const envelope = {};

  // Group by envelope field. Prefer a value the user changed; otherwise keep last.
  const byField = new Map();
  collectClientConfigElements(template).forEach((el) => {
    const binding = el.binding ?? resolveBinding(el, el.segmentId);
    const field = binding ? ENVELOPE_BINDING_TO_FIELD[binding] : null;
    if (!field) return;
    if (
      !Object.prototype.hasOwnProperty.call(clientConfigSamples, el.elementId)
    ) {
      return;
    }
    const value = String(clientConfigSamples[el.elementId] ?? "");
    const hasPrev =
      !!previousSamples &&
      Object.prototype.hasOwnProperty.call(previousSamples, el.elementId);
    const prev = hasPrev ? String(previousSamples[el.elementId] ?? "") : null;
    // Missing previous key counts as edited so dirty saves still patch Client.json.
    const changed = onlyEdited ? !hasPrev || value !== prev : hasPrev && value !== prev;
    const existing = byField.get(field);
    if (!existing || changed) {
      byField.set(field, { value, changed: !!changed });
    } else if (!existing.changed && value.length > existing.value.length) {
      // Same binding on ISA_06 + GS_02: prefer the longer entered value.
      byField.set(field, { value, changed: false });
    }
  });

  byField.forEach((entry, field) => {
    // On dirty sample save, only patch fields the user edited (Client.json merge is partial).
    if (onlyEdited && !entry.changed) return;
    envelope[field] = entry.value;
  });

  return Object.keys(envelope).length > 0 ? envelope : null;
}

/** Propagate a clientConfig edit to every element that shares the same binding. */
export function applyClientConfigSampleChange(
  template,
  clientConfigSamples,
  elementId,
  value
) {
  const next = { ...(clientConfigSamples || {}), [elementId]: value };
  const changed = collectClientConfigElements(template).find(
    (el) => el.elementId === elementId
  );
  const binding = changed?.binding;
  if (!binding) return next;

  collectClientConfigElements(template).forEach((el) => {
    if (el.binding === binding) {
      next[el.elementId] = value;
    }
  });
  return next;
}

export const CLIENT_CONFIG_SAMPLES_STORAGE_KEY =
  "edi-mapping-explorer-client-config-samples";

export function loadClientConfigSamplesLocal() {
  try {
    const raw = localStorage.getItem(CLIENT_CONFIG_SAMPLES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function saveClientConfigSamplesLocal(samples) {
  try {
    localStorage.setItem(
      CLIENT_CONFIG_SAMPLES_STORAGE_KEY,
      JSON.stringify(samples || {})
    );
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

export function enrichSegmentWithVisibility(segment, segmentVisibility) {
  const enriched = enrichSegment(segment);
  const isUserVisible = segment.isOptional
    ? segmentVisibility[segment.segmentId] !== false
    : true;
  return { ...enriched, isUserVisible };
}

export function getVisibleLoops(
  template,
  clientKey,
  showOptional,
  segmentVisibility = null
) {
  const hiddenForClient = CLIENT_OPTIONAL_OVERRIDES[clientKey] ?? [];
  const visibility =
    segmentVisibility ?? getDefaultSegmentVisibility(template);

  return template.loops
    .map((loop) => ({
      ...loop,
      segments: loop.segments
        .filter(
          (seg) =>
            showOptional ||
            (!seg.isOptional && !hiddenForClient.includes(seg.segmentId))
        )
        .map((seg) => enrichSegmentWithVisibility(seg, visibility)),
    }))
    .filter((loop) => loop.segments.length > 0);
}

export function getGuideLoops(loops, showOptionalInGuide = true) {
  return loops
    .map((loop) => ({
      ...loop,
      segments: loop.segments.filter((seg) => {
        if (!seg.isOptional) return true;
        return showOptionalInGuide;
      }),
    }))
    .filter((loop) => loop.segments.length > 0);
}
export function getVisibleElements(elements, showOptional) {
  return elements.filter(
    (el) => showOptional || el.isVisible || el.required
  );
}

export function getSummaryStats(loops, showOptional) {
  let segments = 0;
  let elements = 0;
  let visible = 0;
  let hidden = 0;
  let optionalVisible = 0;
  let optionalHidden = 0;

  loops.forEach((loop) => {
    segments += loop.segments.length;
    loop.segments.forEach((seg) => {
      if (seg.isOptional) {
        if (seg.isUserVisible) optionalVisible += 1;
        else optionalHidden += 1;
      }
      const visibleEls = getVisibleElements(seg.elements, showOptional);
      elements += visibleEls.length;
      visibleEls.forEach((el) => {
        if (el.visibilityStatus === "visible") visible += 1;
        else hidden += 1;
      });
    });
  });

  return {
    segments,
    elements,
    visible,
    hidden,
    optionalVisible,
    optionalHidden,
  };
}

export function filterBySearch(loops, query) {
  if (!query || !query.trim()) return loops;
  const q = query.toLowerCase().trim();

  return loops
    .map((loop) => ({
      ...loop,
      segments: loop.segments.filter((seg) => {
        if (seg.segmentId.toLowerCase().includes(q)) return true;
        if (seg.tag.toLowerCase().includes(q)) return true;
        if (seg.friendlyName?.toLowerCase().includes(q)) return true;
        if (loop.name.toLowerCase().includes(q)) return true;
        if (loop.loopId.toLowerCase().includes(q)) return true;
        return seg.elements.some(
          (el) =>
            el.label.toLowerCase().includes(q) ||
            el.friendlyLabel?.toLowerCase().includes(q) ||
            el.fhirPath?.toLowerCase().includes(q) ||
            el.binding?.toLowerCase().includes(q)
        );
      }),
    }))
    .filter((loop) => loop.segments.length > 0);
}

export function filterElementsBySearch(elements, query) {
  if (!query || !query.trim()) return elements;
  const q = query.toLowerCase().trim();
  return elements.filter(
    (el) =>
      el.label.toLowerCase().includes(q) ||
      el.friendlyLabel?.toLowerCase().includes(q) ||
      el.fhirPath?.toLowerCase().includes(q) ||
      el.binding?.toLowerCase().includes(q) ||
      String(el.value).toLowerCase().includes(q)
  );
}

export function getExplorerLoops(template, segmentVisibility = null) {
  const visibility =
    segmentVisibility ?? getDefaultSegmentVisibility(template);

  return template.loops.map((loop) => ({
    ...loop,
    displayName: getLoopDisplayName(loop),
    segments: loop.segments.map((seg) =>
      enrichSegmentWithVisibility(seg, visibility)
    ),
  }));
}

/** Tokenize a raw EDI segment line for highlighted preview. */
export function getEdiPreviewTokens(segment, mode = "generated") {
  if (segment?.isOptional === true && segment?.isUserVisible === false) {
    return [
      {
        text: "Not generated — optional segment hidden for this request.",
        type: "empty",
      },
    ];
  }

  const line = buildSegmentPreviewLine(segment, mode);
  if (!line?.trim()) {
    return [{ text: "(empty segment)", type: "empty" }];
  }

  return tokenizeEdiLine(line, segment, mode);
}

/** Whether a segment appears in full-transaction EDI preview. */
export function isSegmentIncludedInPreview(segment) {
  if (segment.isOptional === true && segment.isUserVisible === false) {
    return false;
  }
  return true;
}

/** Placeholder token for template-mode EDI (e.g. {Patient.name.family}). */
function formatFhirPlaceholder(element) {
  if (element.fhirPath) {
    const simplified = element.fhirPath
      .replace(/Coverage\.subscriber → Patient\./, "Patient.")
      .replace(/Claim\.provider → Practitioner\./, "Practitioner.")
      .replace(/ → /g, ".");
    return `{${simplified}}`;
  }

  if (element.binding) return `{${element.binding}}`;

  const display = (element.sourceDisplay ?? "").trim();
  if (display && display !== "—" && element.sourceType === "dynamic") {
    return `{${display}}`;
  }

  return `{${element.friendlyLabel ?? element.label}}`;
}

export function getElementPlaceholder(element, segmentId = null) {
  if (element.sourceType === "static") {
    const staticMeta = segmentId
      ? resolveStaticMeta(element.elementId, segmentId)
      : null;
    const candidates = [
      staticMeta?.value,
      element.defaultValue ?? element.DefaultValue,
      element.value,
    ];
    for (const candidate of candidates) {
      if (
        candidate != null &&
        String(candidate).trim() !== "" &&
        !isPlaceholderSampleValue(candidate)
      ) {
        return String(candidate).trim();
      }
    }
    return "";
  }

  if (element.sourceType === "system") {
    const display = (element.sourceDisplay ?? "").trim();
    if (display && display !== "—") return `{${display}}`;
    return "{System}";
  }

  return formatFhirPlaceholder(element);
}

function normalizeEdiLine(line) {
  const trimmed = (line ?? "").trim();
  if (!trimmed) return "";
  return trimmed.endsWith("~") ? trimmed : `${trimmed}~`;
}

/** Build one segment line for template or generated preview. */
export function buildSegmentPreviewLine(segment, mode) {
  if (segment?.isOptional === true && segment?.isUserVisible === false) {
    return "";
  }

  if (!segment.elements?.length) {
    if (mode === "generated" && segment.raw?.trim()) {
      return normalizeEdiLine(segment.raw);
    }
    return segment.tag ? `${segment.tag}~` : "";
  }

  const positions = segment.elements
    .map((el) => el.position ?? 0)
    .filter((p) => p > 0);
  if (positions.length === 0) {
    if (mode === "template") {
      return segment.tag ? `${segment.tag}~` : "";
    }
    if (segment.raw?.trim()) {
      return normalizeEdiLine(segment.raw);
    }
    return segment.tag ? `${segment.tag}~` : "";
  }

  const maxPos = Math.max(...positions);
  const byPos = {};
  segment.elements.forEach((el) => {
    const pos = el.position ?? 0;
    if (pos > 0) byPos[pos] = el;
  });

  const parts = [segment.tag];
  for (let pos = 1; pos <= maxPos; pos += 1) {
    const el = byPos[pos];
    if (!el) {
      parts.push("");
      continue;
    }
    if (mode === "template") {
      parts.push(getElementPlaceholder(el, segment.segmentId));
    } else {
      parts.push(
        resolveElementSampleValue(el, segment.segmentId, segment.tag)
      );
    }
  }

  while (parts.length > 1 && parts[parts.length - 1] === "") {
    parts.pop();
  }

  return `${parts.join("*")}~`;
}

/** Tokenize a full segment line for syntax highlighting. */
export function tokenizeEdiLine(line, segment, mode = "generated") {
  if (!line?.trim()) {
    return [{ text: "(empty segment)", type: "empty" }];
  }

  const pieces = line.replace(/~$/u, "").split("*");
  const tokens = [{ text: pieces[0], type: "tag" }];

  for (let i = 1; i < pieces.length; i += 1) {
    tokens.push({ text: "*", type: "delimiter" });
    const piece = pieces[i];
    if (piece === "") {
      tokens.push({ text: "", type: "empty-field" });
      continue;
    }

    if (mode === "template" && piece.startsWith("{") && piece.endsWith("}")) {
      tokens.push({ text: piece, type: "placeholder" });
      continue;
    }

    const byPosition = segment?.elements?.find((el) => (el.position ?? 0) === i);
    const byValue = segment?.elements?.find(
      (el) => String(el.value ?? "").trim() === piece.trim()
    );
    const match = byPosition ?? byValue;
    tokens.push({
      text: piece,
      type:
        match?.sourceType ??
        (mode === "template" ? "static" : "value"),
      elementId: match?.elementId,
    });
  }

  tokens.push({ text: "~", type: "delimiter" });
  return tokens;
}

/**
 * Build grouped full-transaction EDI preview from explorer loops.
 * @returns {{ loopId, loopName, segments: { segmentId, tag, friendlyName, line, tokens }[] }[]}
 */
export function buildFullEdiPreview(loops, mode = "generated") {
  return (loops ?? [])
    .map((loop) => {
      const segments = (loop.segments ?? [])
        .filter(isSegmentIncludedInPreview)
        .map((seg) => {
          const line = buildSegmentPreviewLine(seg, mode);
          return {
            segmentId: seg.segmentId,
            tag: seg.tag,
            friendlyName: seg.friendlyName ?? seg.segmentId,
            line,
            tokens: tokenizeEdiLine(line, seg, mode),
          };
        })
        .filter((entry) => entry.line);

      return {
        loopId: loop.loopId,
        loopName: loop.displayName ?? loop.name ?? loop.loopId,
        segments,
      };
    })
    .filter((group) => group.segments.length > 0);
}

/** Flatten preview groups to plain text for copy/download. */
export function buildFullEdiText(previewGroups) {
  return previewGroups
    .flatMap((group) => group.segments.map((seg) => seg.line))
    .join("\n");
}

export { EDI_TEMPLATE };
